// The workshop's delve-sim: one worker for previews and one for long runs (validation), so a validation never delays
// a map. Both load the shown pack's biome catalog (biomes.json, the engine's export).
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { fetchJson, packUrl } from "@/features/workshop/data";
import { usePack } from "@/features/workshop/pack";
import { SimClient } from "./client";
import type { Generator, SimBiome } from "./protocol";

export interface SimState {
  preview: SimClient;
  runs: SimClient;
  status: "loading" | "ready" | "missing" | "error";
  error?: string;
  biomes: SimBiome[];
  generators: Generator[];
  /** Instance mode settings (fields, defaults); null with a delve-sim older than protocol 2. */
  instance: Generator | null;
  /** Resolves when the pack's export is loaded in that client. */
  ready(client: SimClient): Promise<void>;
}

const SimContext = createContext<SimState | null>(null);

export function SimProvider({ children }: { children: ReactNode }) {
  const { pack } = usePack();
  const clients = useMemo(() => ({ preview: new SimClient(), runs: new SimClient() }), []);
  useEffect(() => () => { clients.preview.dispose(); clients.runs.dispose(); }, [clients]);

  const exported = useQuery({
    queryKey: ["workshop", pack, "biomes.json"],
    queryFn: () => fetchJson<object>(packUrl(pack, "biomes.json")),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const loaded = useQuery({
    queryKey: ["sim", pack, exported.dataUpdatedAt],
    queryFn: async () => {
      await clients.preview.ensureLoaded(pack, exported.data!);
      const [biomes, generators, instance] = await Promise.all([
        clients.preview.request("biomes", {}),
        clients.preview.request("generators", {}),
        clients.preview.request("instanceSettings", {}).catch(() => null),
      ]);
      return { biomes, generators, instance };
    },
    enabled: exported.isSuccess,
    staleTime: Infinity,
    retry: false,
  });

  const value = useMemo<SimState>(() => {
    const missing = exported.isError && (exported.error as { status?: number }).status === 404;
    const status = missing ? "missing" : exported.isError || loaded.isError ? "error" : loaded.isSuccess ? "ready" : "loading";
    const failure = exported.error ?? loaded.error;
    return {
      ...clients,
      status,
      ...(failure && !missing ? { error: failure.message } : {}),
      biomes: loaded.data?.biomes ?? [],
      generators: loaded.data?.generators ?? [],
      instance: loaded.data?.instance ?? null,
      async ready(client) {
        if (!exported.data) throw new Error("No biome catalog for this pack.");
        await client.ensureLoaded(pack, exported.data);
      },
    };
  }, [clients, exported.data, exported.isError, exported.error, loaded.isSuccess, loaded.isError, loaded.error, loaded.data, pack]);

  return <SimContext.Provider value={value}>{children}</SimContext.Provider>;
}

export function useSim(): SimState {
  const value = useContext(SimContext);
  if (!value) throw new Error("useSim outside <SimProvider>");
  return value;
}
